import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  Network, 
  Shield, 
  Server, 
  Users, 
  ArrowRight, 
  CheckCircle,
  Phone,
  Monitor,
  Cloud,
  Lock,
  Zap,
  Headphones
} from "lucide-react";

const Home = () => {
  const services = [
    {
      icon: Server,
      title: "IT-Infrastruktur",
      description: "Moderne und skalierbare IT-Systeme für Ihr Unternehmen",
      features: ["Server & Storage", "Virtualisierung", "Backup-Lösungen"],
      image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31"
    },
    {
      icon: Shield,
      title: "IT-Sicherheit",
      description: "Umfassender Schutz vor Cyberbedrohungen und Datenverlust",
      features: ["Firewall Management", "Antivirus-Lösungen", "Security Monitoring"],
      image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3"
    },
    {
      icon: Network,
      title: "Netzwerkmanagement",
      description: "Zuverlässige und performante Netzwerkinfrastrukturen",
      features: ["LAN/WAN Setup", "WiFi-Lösungen", "Netzwerk-Monitoring"],
      image: "https://images.unsplash.com/photo-1551808525-51a94da548ce"
    },
    {
      icon: Users,
      title: "IT-Beratung",
      description: "Strategische Beratung für Ihre digitale Transformation",
      features: ["IT-Strategie", "Technologie-Beratung", "Prozessoptimierung"],
      image: "https://images.unsplash.com/photo-1557804506-669a67965ba0"
    }
  ];

  const benefits = [
    "Professioneller IT-Support",
    "Zertifizierte IT-Experten", 
    "Individuelle Lösungen",
    "Transparente Preismodelle",
    "Schnelle Reaktionszeiten",
    "Compliance & Datenschutz"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-gray-100"></div>
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1550751827-4bd374c3f58b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHw0fHxuZXR3b3JrJTIwc2VjdXJpdHl8ZW58MHx8fHwxNzU4MTY3NDM5fDA&ixlib=rb-4.1.0&q=85')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200">
                  <Network className="mr-1 h-3 w-3" />
                  Ihr IT-Partner in Deutschland
                </Badge>
                <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight">
                  Professionelle{" "}
                  <span className="text-blue-600">IT-Services</span>{" "}
                  & Netzwerktechnik in Deutschland
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  Professionelle IT-Services, Netzwerktechnik und Cybersicherheit 
                  für Unternehmen in Deutschland.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" asChild className="bg-blue-600 hover:bg-blue-700">
                  <Link to="/contact">
                    <Phone className="mr-2 h-5 w-5" />
                    Beratung anfragen
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <Link to="/services">
                    Leistungen entdecken
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </div>

              <div className="grid grid-cols-3 gap-8 pt-8 border-t border-gray-200">
                <div>
                  <div className="text-2xl font-bold text-blue-600">500+</div>
                  <div className="text-sm text-gray-600">Zufriedene Kunden</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-blue-600">5★</div>
                  <div className="text-sm text-gray-600">Kundenbewertung</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-blue-600">15+</div>
                  <div className="text-sm text-gray-600">Jahre Erfahrung</div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1709715357520-5e1047a2b691"
                  alt="Technology Collaboration - IT Team Working Together"
                  className="w-full h-[600px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/20 to-transparent"></div>
              </div>
              
              {/* Floating Cards */}
              <div className="absolute -top-6 -left-6 bg-white p-4 rounded-xl shadow-lg border">
                <div className="flex items-center space-x-3">
                  <Shield className="h-8 w-8 text-green-600" />
                  <div>
                    <div className="font-semibold text-sm">99.9% Uptime</div>
                    <div className="text-xs text-gray-500">Garantiert</div>
                  </div>
                </div>
              </div>
              
              <div className="absolute -bottom-6 -right-6 bg-white p-4 rounded-xl shadow-lg border">
                <div className="flex items-center space-x-3">
                  <Headphones className="h-8 w-8 text-blue-600" />
                  <div>
                    <div className="font-semibold text-sm">IT-Support</div>
                    <div className="text-xs text-gray-500">Professionell & schnell</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <Badge className="bg-blue-100 text-blue-800">IT-Dienstleistungen</Badge>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
              Unsere Kernkompetenzen
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Von der IT-Infrastruktur bis zur Cybersicherheit – wir bieten 
              Ihnen alle Leistungen aus einer Hand für Ihren digitalen Erfolg.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1 overflow-hidden">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <service.icon className="h-8 w-8 text-white" />
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="text-xl">{service.title}</CardTitle>
                  <CardDescription className="text-gray-600">
                    {service.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-600">
                        <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button size="lg" asChild>
              <Link to="/services">
                Alle Leistungen ansehen
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1573166364366-3f4f8b1857ea"
                alt="Professional IT Consulting Team"
                className="w-full rounded-2xl shadow-xl"
              />
            </div>
            
            <div className="space-y-8">
              <div className="space-y-4">
                <Badge className="bg-green-100 text-green-800">Warum NetSyTe?</Badge>
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                  Ihr vertrauensvoller IT-Partner
                </h2>
                <p className="text-xl text-gray-600">
                  Mit über 15 Jahren Erfahrung in der IT-Branche verstehen wir 
                  die Herausforderungen von Unternehmen und bieten maßgeschneiderte Lösungen.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>

              <Button size="lg" asChild>
                <Link to="/references">
                  Referenzen ansehen
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Showcase Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <Badge className="bg-purple-100 text-purple-800">Technologien</Badge>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
              Unsere Technologie-Expertise
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Wir arbeiten mit führenden Technologien und Herstellern, um Ihnen die besten Lösungen zu bieten
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
            {[
              { name: "Microsoft", logo: "🏢" },
              { name: "VMware", logo: "☁️" },
              { name: "Cisco", logo: "🔗" },
              { name: "Dell", logo: "💻" },
              { name: "Fortinet", logo: "🛡️" },
              { name: "Azure", logo: "☁️" },
            ].map((tech, index) => (
              <div key={index} className="text-center group">
                <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 group-hover:-translate-y-2">
                  <div className="text-4xl mb-3 group-hover:scale-110 transition-transform">
                    {tech.logo}
                  </div>
                  <div className="font-semibold text-gray-900">{tech.name}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <Badge className="bg-green-100 text-green-800">Kundenstimmen</Badge>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
              Was unsere Kunden sagen
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Vertrauen Sie auf die Erfahrungen unserer zufriedenen Kunden
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="p-6 hover:shadow-lg transition-shadow">
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <CheckCircle key={i} className="h-4 w-4 text-yellow-500" />
                  ))}
                </div>
                <p className="text-gray-600 italic">
                  "NetSyTe hat unsere gesamte IT-Infrastruktur modernisiert. Die professionelle 
                  Betreuung und schnelle Reaktionszeiten überzeugen uns täglich."
                </p>
                <div className="border-t pt-4">
                  <p className="font-semibold text-gray-900">Autohaus Wilke GmbH</p>
                  <p className="text-sm text-gray-500">Geschäftsführung</p>
                </div>
              </CardContent>
            </Card>

            <Card className="p-6 hover:shadow-lg transition-shadow">
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <CheckCircle key={i} className="h-4 w-4 text-yellow-500" />
                  ))}
                </div>
                <p className="text-gray-600 italic">
                  "Dank NetSyTe konnten wir unsere Cybersicherheit erheblich verbessern. 
                  Das professionelle Monitoring gibt uns die Sicherheit, die wir brauchen."
                </p>
                <div className="border-t pt-4">
                  <p className="font-semibold text-gray-900">Admin Hausverwaltung</p>
                  <p className="text-sm text-gray-500">IT-Leitung</p>
                </div>
              </CardContent>
            </Card>

            <Card className="p-6 hover:shadow-lg transition-shadow">
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <CheckCircle key={i} className="h-4 w-4 text-yellow-500" />
                  ))}
                </div>
                <p className="text-gray-600 italic">
                  "Die Cloud-Migration war dank NetSyTe reibungslos. Unsere Produktivität 
                  hat sich deutlich gesteigert und die Kosten sind gesunken."
                </p>
                <div className="border-t pt-4">
                  <p className="font-semibold text-gray-900">Mittelständisches Unternehmen</p>
                  <p className="text-sm text-gray-500">CTO</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            <div className="space-y-2">
              <div className="text-4xl lg:text-5xl font-bold text-white">500+</div>
              <div className="text-blue-100">Zufriedene Kunden</div>
            </div>
            <div className="space-y-2">
              <div className="text-4xl lg:text-5xl font-bold text-white">99.9%</div>
              <div className="text-blue-100">Uptime Garantie</div>
            </div>
            <div className="space-y-2">
              <div className="text-4xl lg:text-5xl font-bold text-white">100%</div>
              <div className="text-blue-100">Zufriedenheit</div>
            </div>
            <div className="space-y-2">
              <div className="text-4xl lg:text-5xl font-bold text-white">15+</div>
              <div className="text-blue-100">Jahre Erfahrung</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-8">
            <h2 className="text-3xl lg:text-4xl font-bold text-white">
              Bereit für Ihre digitale Transformation?
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Lassen Sie uns gemeinsam Ihre IT-Infrastruktur auf das nächste Level bringen. 
              Kontaktieren Sie uns für eine kostenlose Beratung.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700" asChild>
                <Link to="/contact">
                  <Phone className="mr-2 h-5 w-5" />
                  Kostenlose Beratung
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white" asChild>
                <Link to="/cyber-risiko-check">
                  <Shield className="mr-2 h-5 w-5" />
                  CyberRisikoCheck starten
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;